import { FileUploadOutlined } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { Button } from "@mui/material";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import React, { ReactElement, useRef, useState } from "react";

import {
  FieldFilterDefinition,
  RenderingObjectKind,
} from "../../../generated/gql/graphql";
import useBackgroundFilters from "../../../hooks/use-background-filters";
import { useCreateBackground } from "../../../hooks/use-create-background";
import useLocale from "../../../hooks/use-locale";
import useProductFilters from "../../../hooks/use-product-filters";
import AssetUploadModal from "../../../modals/asset-upload-modal";
import { BackgroundListRef } from "../../backgrounds/background-list";
import BackgroundsPanel from "../../backgrounds/backgrounds-panel";
import { useCreateGraphic } from "../../graphics/create-graphic";
import { GraphicListRef } from "../../graphics/graphic-list";
import GraphicsPanel from "../../graphics/graphics-panel";
import useGraphicFilters from "../../graphics/use-graphic-filters";
import SearchInput from "../../inputs/search-input";
import Filters from "../../lists/filters";
import GridSizer from "../../lists/grid-sizer";
import Panel from "../../panels/v2/panel";
import PanelContent from "../../panels/v2/panel-content";
import PanelFooter from "../../panels/v2/panel-footer";
import PanelHeader from "../../panels/v2/panel-header";
import { TextsPanel, TextsPanelProps } from "../../texts/texts-panel";

import { CanvasObject } from "./canvas-object";
import ProductsPanel, { ProductsPanelProps } from "./products-panel";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

type TabConfig = {
  id: number;
  label: string;
  supportsText: boolean;
  supportsSearch: boolean;
  supportsUpload: boolean;
  supportsFilters: boolean;
  availableFilters: FieldFilterDefinition[];
  selectedFilters: FieldFilterDefinition[];
  onChangeFilters: (filters: FieldFilterDefinition[]) => void;
};

function CustomTabPanel(props: TabPanelProps): ReactElement {
  const { children, value, index, ...other } = props;

  return (
    <div
      className="h-full min-h-0 flex-1 overflow-hidden"
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <>{children}</>}
    </div>
  );
}

export type AssetsAreaProps = {
  backgroundId: string | undefined;
  setBackgroundId: (id: string | undefined) => void;
  setShowTextAi: (value: boolean) => void;
  onGraphicAdded: (graphicId: string, width: number, height: number) => void;
  showTextAi: boolean;
  onShowTextAi: (show: boolean) => void;
  selectedCanvasObject: CanvasObject | undefined;
  selectedObjectChanged: (changes: Partial<CanvasObject>) => void;
  selectedCanvasObjectIndex: number;
  canvasObjects: CanvasObject[];
  objectChanged: (
    index: number,
    kind: RenderingObjectKind,
    changes: Partial<CanvasObject>,
  ) => void;
} & Pick<TextsPanelProps, "onAddText"> &
  Pick<ProductsPanelProps, "selectedProductId" | "productSelected">;

export function AssetsArea({
  backgroundId,
  setBackgroundId,
  setShowTextAi,
  onGraphicAdded,
  onAddText,
  showTextAi,
  onShowTextAi,
  selectedCanvasObject: selectedObject,
  selectedObjectChanged,
  selectedProductId,
  selectedCanvasObjectIndex,
  canvasObjects,
  productSelected,
  objectChanged,
}: AssetsAreaProps): ReactElement {
  const TabIndexBackgrounds = 0;
  const backgroundListRef = useRef<BackgroundListRef>(null);
  const createBackground = useCreateBackground();

  const TabIndexGraphics = 1;
  const graphicsListRef = useRef<GraphicListRef>(null);
  const createGraphic = useCreateGraphic();

  const TabIndexTexts = 2;
  const TabIndexProducts = 3;

  const [selectedTabIndex, setSelectedTabIndex] = useState(TabIndexBackgrounds);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [gridSize, setGridSize] = useState<number>(100);
  const [showAiBackground, setShowAiBackground] = useState(false);
  const [activeBackgroundFilters, setActiveBackgroundFilters] = useState<
    FieldFilterDefinition[]
  >([]);
  const [activeGraphicFilters, setActiveGraphicFilters] = useState<
    FieldFilterDefinition[]
  >([]);
  const [activeProductFilters, setActiveProductFilters] = useState<
    FieldFilterDefinition[]
  >([]);

  const { fieldFilters: availableBackgroundFilters } = useBackgroundFilters();
  const { fieldFilters: availableGraphicFilters } = useGraphicFilters();
  const { options: availableProductFilters } = useProductFilters();

  const { getText } = useLocale();
  const emptyFilters: FieldFilterDefinition[] = [];
  const handleEmptyFiltersChange = (): void => {
    return;
  };

  const tabConfigs: TabConfig[] = [
    {
      id: TabIndexBackgrounds,
      label: getText("sections.backgroundsPanel.headline"),
      supportsSearch: true,
      supportsText: false,
      supportsUpload: true,
      supportsFilters: true,
      availableFilters: availableBackgroundFilters,
      selectedFilters: activeBackgroundFilters,
      onChangeFilters: setActiveBackgroundFilters,
    },
    {
      id: TabIndexGraphics,
      label: getText("sections.graphicsPanel.headline"),
      supportsSearch: true,
      supportsText: false,
      supportsUpload: true,
      supportsFilters: true,
      availableFilters: availableGraphicFilters,
      selectedFilters: activeGraphicFilters,
      onChangeFilters: setActiveGraphicFilters,
    },
    {
      id: TabIndexTexts,
      label: getText("sections.textsPanel.headline"),
      supportsSearch: false,
      supportsText: true,
      supportsUpload: false,
      supportsFilters: false,
      availableFilters: emptyFilters,
      selectedFilters: emptyFilters,
      onChangeFilters: handleEmptyFiltersChange,
    },
    {
      id: TabIndexProducts,
      label: "Products",
      supportsSearch: true,
      supportsText: false,
      supportsUpload: false,
      supportsFilters: true,
      availableFilters: availableProductFilters,
      selectedFilters: activeProductFilters,
      onChangeFilters: setActiveProductFilters,
    },
  ];

  const currentTabIndex = showTextAi ? TabIndexTexts : selectedTabIndex;
  const currentTabConfig =
    tabConfigs.find((tabConfig) => tabConfig.id === currentTabIndex) ??
    tabConfigs[TabIndexBackgrounds];

  const handleSelectedTabIndexChange = (
    _event: React.SyntheticEvent,
    newValue: number,
  ): void => {
    onShowTextAi(false);
    setSelectedTabIndex(newValue);
  };

  const createAsset =
    currentTabIndex === TabIndexBackgrounds
      ? async (name: string, file: File) => {
          const response = await createBackground({
            input: {
              name,
              image: file,
            },
          });

          const graphQLError = response.errors?.at(0);
          if (graphQLError) {
            throw new Error();
          }

          const result = response.data?.createBackground;
          if (result?.__typename === "CreateBackgroundError") {
            throw new Error(result.message);
          }
        }
      : async (name: string, file: File) => {
          const response = await createGraphic({
            input: {
              name,
              file,
            },
          });

          const graphQLError = response.errors?.at(0);
          if (graphQLError) {
            throw new Error();
          }

          const result = response.data?.createGraphic;
          if (result?.__typename === "CreateGraphicError") {
            throw new Error(result.message);
          }
        };

  const assetUploadHeadline =
    currentTabIndex === TabIndexBackgrounds
      ? getText("sections.assetUpload.background.headline")
      : getText("sections.assetUpload.graphic.headline");

  const formNameExample =
    currentTabIndex === TabIndexBackgrounds
      ? getText("sections.assetUpload.background.form.nameExample")
      : getText("sections.assetUpload.graphic.form.nameExample");

  function handleCloseAssetUpload(): void {
    setIsUploadOpen(false);

    switch (currentTabIndex) {
      case TabIndexBackgrounds:
        backgroundListRef.current?.refetch();
        break;
      case TabIndexGraphics:
        graphicsListRef.current?.refetch();
        break;
      default:
        break;
    }
  }

  // Indicates whether to show a list. A list view includes the filters and grid sizer.
  const listView = !(
    (currentTabIndex === TabIndexBackgrounds && showAiBackground) ||
    (currentTabIndex === TabIndexTexts && showTextAi)
  );

  return (
    <section className="h-full max-h-full min-h-0 overflow-hidden">
      <Panel>
        <PanelHeader>
          <Tabs
            value={currentTabIndex}
            onChange={handleSelectedTabIndexChange}
            aria-label="editor asset tabs"
            className="min-h-fit"
            sx={{
              "& .MuiButtonBase-root.Mui-selected": {
                color: "#1B1B1B",
              },
              "& .MuiTabs-indicator": {
                backgroundColor: "#1B1B1B",
              },
            }}
          >
            {tabConfigs.map((tabConfig) => (
              <Tab
                key={tabConfig.id}
                className="text-[16px] text-[#A1A1A1] font-bold px-0 py-[10px] mr-[20px] min-w-fit min-h-fit"
                label={tabConfig.label}
              />
            ))}
          </Tabs>
        </PanelHeader>
        {listView && (
          <PanelHeader classNames="relative z-10">
            <div className="flex w-full flex-col gap-3">
              <div className="flex w-full items-center justify-between gap-3">
                {currentTabConfig.supportsSearch && (
                  <SearchInput
                    className="max-w-[240px]"
                    value={searchQuery}
                    onChange={setSearchQuery}
                  />
                )}
                {currentTabConfig.supportsUpload && (
                  <div>
                    {currentTabIndex === TabIndexBackgrounds && (
                      <Button
                        variant="contained"
                        color="ai"
                        endIcon={<AutoAwesomeIcon />}
                        onClick={() => setShowAiBackground(true)}
                      >
                        Create
                      </Button>
                    )}
                    <Button
                      className="ml-3 text-[#4A4A4A] font-bold border-[#4A4A4A] hover:border-[#4A4A4A] rounded-md"
                      variant="outlined"
                      endIcon={
                        <FileUploadOutlined className="!text-[#4A4A4A]" />
                      }
                      onClick={() => setIsUploadOpen(true)}
                    >
                      Upload
                    </Button>
                  </div>
                )}
                {currentTabConfig.supportsText && (
                  <div className="flex justify-end w-full">
                    <Button
                      className="ml-3 text-[#4A4A4A] font-bold border-[#4A4A4A] hover:border-[#4A4A4A] rounded-md"
                      variant="outlined"
                      endIcon={<AddIcon />}
                      onClick={onAddText}
                    >
                      Add text
                    </Button>
                  </div>
                )}
              </div>
              {currentTabConfig.supportsFilters && (
                <div className="w-full">
                  <Filters
                    filters={currentTabConfig.availableFilters}
                    selectedFilters={currentTabConfig.selectedFilters}
                    onChangeFilters={currentTabConfig.onChangeFilters}
                  />
                </div>
              )}
            </div>
          </PanelHeader>
        )}
        <PanelContent>
          <CustomTabPanel value={currentTabIndex} index={TabIndexBackgrounds}>
            <BackgroundsPanel
              selectedBackgroundId={backgroundId}
              selectedBackgroundIdChange={setBackgroundId}
              query={searchQuery}
              filters={activeBackgroundFilters}
              gridSize={gridSize}
              backgroundListRef={backgroundListRef}
              showAiBackground={showAiBackground}
              onCloseAiBackground={() => setShowAiBackground(false)}
            />
          </CustomTabPanel>
          <CustomTabPanel value={currentTabIndex} index={TabIndexGraphics}>
            <GraphicsPanel
              handleGraphicClick={onGraphicAdded}
              query={searchQuery}
              filters={activeGraphicFilters}
              gridSize={gridSize}
              graphicListRef={graphicsListRef}
            />
          </CustomTabPanel>
          <CustomTabPanel value={currentTabIndex} index={TabIndexTexts}>
            <TextsPanel
              onAddText={onAddText}
              canvasObjects={canvasObjects}
              objectChanged={objectChanged}
            />
          </CustomTabPanel>
          <CustomTabPanel value={currentTabIndex} index={TabIndexProducts}>
            <ProductsPanel
              selectedProductId={selectedProductId}
              productSelected={productSelected}
              query={searchQuery}
              filters={activeProductFilters}
              gridSize={gridSize}
              canvasObjects={canvasObjects}
            />
          </CustomTabPanel>
        </PanelContent>
        {listView && currentTabIndex !== TabIndexTexts && (
          <PanelFooter classNames="py-[20px] border-t border-[#CECFD2] px-4">
            <GridSizer
              classNames="p-0 m-0 flex justify-end space-x-2"
              gridSize={gridSize}
              setGridSize={setGridSize}
              step={10}
              minGridSize={50}
              maxGridSize={150}
            />
          </PanelFooter>
        )}
      </Panel>
      <AssetUploadModal
        headline={assetUploadHeadline}
        open={isUploadOpen}
        onClose={() => handleCloseAssetUpload()}
        createAsset={createAsset}
        formNameExample={formNameExample}
      />
    </section>
  );
}
